/**
 * 経費管理表(モックアップ v1.3 準拠)。
 * 事業部 → 拠点 → 科目 のドリルダウン月次台帳。
 * 固定/変動はバッジ+表示フィルタ、確定/見込み/合計の3列。
 * 「取引を見る」は固定・変動どちらの行にも表示し、根拠の取引と証憑まで辿れる。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMasters } from '@core/masters/MastersProvider';
import { usePeriod } from '@core/components/PeriodProvider';
import { useToast } from '@core/components/Toast';
import Modal from '@core/components/Modal';
import { toMonthLabel, toShortDate } from '@core/lib/date';
import { formatYen } from '@core/lib/money';
import { fetchUsers } from '@core/masters/masterRepo';
import { fetchHierarchyNodes } from '@core/masters/hierarchyRepo';
import AttachmentLink from '@core/expense/AttachmentLink';
import {
  applyFixedFilter,
  buildLedger,
  fetchLedgerTransactions,
  type FixedFilter,
  type LedgerData,
  type LedgerAccountRow,
} from './ledgerData';
import { STATUS_LABELS, type HierarchyNode, type Transaction } from '@core/lib/types';

export default function LedgerPage() {
  const { departments, accounts, departmentName } = useMasters();
  const { month } = usePeriod();
  const toast = useToast();

  const [raw, setRaw] = useState<LedgerData | null>(null);
  const [filter, setFilter] = useState<FixedFilter>('all');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [evidence, setEvidence] = useState<LedgerAccountRow | null>(null);
  const [userNames, setUserNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (accounts.length === 0 || departments.length === 0) return;
    setLoading(true);
    try {
      const active = departments.filter((d) => d.active);
      const [transactions, users, ...nodeLists] = await Promise.all([
        fetchLedgerTransactions(month),
        fetchUsers().catch(() => []),
        ...active.map((d) => fetchHierarchyNodes(d.id).catch(() => [] as HierarchyNode[])),
      ]);
      const nodesByDepartment = new Map<string, HierarchyNode[]>(
        active.map((d, i) => [d.id, nodeLists[i] ?? []]),
      );
      setRaw(
        buildLedger({ month, transactions, departments: active, nodesByDepartment, accounts }),
      );
      setUserNames(new Map(users.map((u) => [u.id, u.display_name])));
    } catch (e) {
      toast(e instanceof Error ? e.message : '経費管理表を取得できませんでした');
    } finally {
      setLoading(false);
    }
  }, [accounts, departments, month, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const data = useMemo(() => (raw ? applyFixedFilter(raw, filter) : null), [raw, filter]);

  function toggle(key: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (loading || !data) {
    return (
      <section className="screen">
        <div className="card">
          <p className="empty">読み込み中…</p>
        </div>
      </section>
    );
  }

  return (
    <section className="screen" style={{ maxWidth: 1200 }}>
      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          経費管理表({toMonthLabel(data.month)}・出金)
          <span className="hint" style={{ fontWeight: 400 }}>
            — 事業部 → 拠点 → 科目 の順に掘れます。繰り返し支払いは自動で支払い待ちになります
          </span>
          <select
            style={{
              marginLeft: 'auto',
              padding: '6px 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 13,
            }}
            value={filter}
            onChange={(e) => setFilter(e.target.value as FixedFilter)}
          >
            <option value="all">すべて</option>
            <option value="fixed">固定のみ</option>
            <option value="variable">変動のみ</option>
          </select>
        </h3>

        <table>
          <thead>
            <tr>
              <th style={{ width: '38%' }}>項目</th>
              <th style={{ width: 70 }}>固/変</th>
              <th className="num">確定</th>
              <th className="num">見込み</th>
              <th className="num">合計</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ fontWeight: 700 }}>全社出金合計</td>
              <td />
              <td className="num">{formatYen(data.totalConfirmed)}</td>
              <td className="num">{data.totalPending ? formatYen(data.totalPending) : '—'}</td>
              <td className="num" style={{ fontWeight: 700 }}>
                {formatYen(data.totalConfirmed + data.totalPending)}
              </td>
            </tr>

            {data.departments.map((dept) => {
              const deptKey = `dept:${dept.department.id}`;
              const deptOpen = open.has(deptKey);
              return (
                <FragmentRows key={deptKey}>
                  <tr className="pl-row" style={{ cursor: 'pointer' }} onClick={() => toggle(deptKey)}>
                    <td>
                      <div className="pl-name" style={{ paddingLeft: 22 }}>
                        <span className={`chev${deptOpen ? ' open' : ''}`}>▶</span>
                        <span className="lvl">事業部</span>
                        {departmentName(dept.department.id)}
                      </div>
                    </td>
                    <td />
                    <td className="num">{formatYen(dept.confirmed)}</td>
                    <td className="num">{dept.pending ? formatYen(dept.pending) : '—'}</td>
                    <td className="num" style={{ fontWeight: 600 }}>
                      {formatYen(dept.confirmed + dept.pending)}
                    </td>
                  </tr>

                  {deptOpen &&
                    dept.rows.map((row) => {
                      if (row.kind !== 'node') return null;
                      const nodeOpen = open.has(row.key);
                      return (
                        <FragmentRows key={row.key}>
                          <tr
                            className="pl-row"
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggle(row.key)}
                          >
                            <td>
                              <div className="pl-name" style={{ paddingLeft: 44 }}>
                                <span className={`chev${nodeOpen ? ' open' : ''}`}>▶</span>
                                <span className="lvl">{row.levelLabel || '拠点'}</span>
                                {row.label}
                              </div>
                            </td>
                            <td />
                            <td className="num">{formatYen(row.confirmed)}</td>
                            <td className="num">{row.pending ? formatYen(row.pending) : '—'}</td>
                            <td className="num" style={{ fontWeight: 600 }}>
                              {formatYen(row.confirmed + row.pending)}
                            </td>
                          </tr>

                          {nodeOpen &&
                            row.children.map((child) =>
                              child.kind === 'account' ? (
                                <tr key={child.key}>
                                  <td>
                                    <div className="pl-name" style={{ paddingLeft: 66 }}>
                                      <span className="lvl">科目</span>
                                      {child.accountName}
                                      <button
                                        className="txn-link"
                                        onClick={() => setEvidence(child)}
                                      >
                                        取引を見る
                                      </button>
                                    </div>
                                  </td>
                                  <td>
                                    <span className={`badge ${child.isFixed ? 'b-plan' : 'b-pend'}`}>
                                      {child.isFixed ? '固定' : '変動'}
                                    </span>
                                  </td>
                                  <td className="num">{formatYen(child.confirmed)}</td>
                                  <td className="num">
                                    {child.pending ? formatYen(child.pending) : '—'}
                                  </td>
                                  <td className="num" style={{ fontWeight: 600 }}>
                                    {formatYen(child.confirmed + child.pending)}
                                  </td>
                                </tr>
                              ) : null,
                            )}
                        </FragmentRows>
                      );
                    })}
                </FragmentRows>
              );
            })}
          </tbody>
        </table>

        <p className="hint" style={{ marginTop: 10 }}>
          同じ科目(例: 地代家賃)は拠点・事業部をまたいで下の表で全社合計できます。
          「取引を見る」で根拠(個別取引・証憑)まで到達できます。見込みは承認済・提出済でまだ確定していない分です。
        </p>
      </div>

      <div className="card">
        <h3>科目の全社横断合計</h3>
        {data.byAccount.length === 0 ? (
          <p className="empty">この月の出金はまだありません。</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>科目</th>
                <th className="num">確定</th>
                <th className="num">見込み</th>
                <th className="num">合計</th>
              </tr>
            </thead>
            <tbody>
              {data.byAccount.map((row) => (
                <tr key={row.accountId}>
                  <td>{row.accountName}</td>
                  <td className="num">{formatYen(row.confirmed)}</td>
                  <td className="num">{row.pending ? formatYen(row.pending) : '—'}</td>
                  <td className="num" style={{ fontWeight: 600 }}>
                    {formatYen(row.confirmed + row.pending)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {evidence && (
        <Modal onClose={() => setEvidence(null)} width="min(820px, 94vw)">
          <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 12, flexShrink: 0 }}>
            <div>
              <h3>取引一覧(根拠)</h3>
              <div className="path" style={{ margin: '2px 0 0' }}>
                {evidence.accountName}({evidence.isFixed ? '固定' : '変動'})/{' '}
                {evidence.transactions.length}件
              </div>
            </div>
          </div>
          <div style={{ overflow: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>計上日</th>
                  <th>摘要</th>
                  <th className="num">金額</th>
                  <th>状態</th>
                  <th>証憑</th>
                  <th>入力者</th>
                </tr>
              </thead>
              <tbody>
                {evidence.transactions
                  .slice()
                  .sort((a, b) => (a.posting_date < b.posting_date ? 1 : -1))
                  .map((t: Transaction) => (
                    <tr key={t.id}>
                      <td>{toShortDate(t.posting_date)}</td>
                      <td>{t.description ?? ''}</td>
                      <td className="num">{formatYen(t.amount)}</td>
                      <td>
                        <span
                          className={`badge ${(t.status === 'paid' || t.status === 'completed') ? 'b-ok' : 'b-pend'}`}
                        >
                          {STATUS_LABELS[t.status]}
                        </span>
                      </td>
                      <td>
                        {t.receipts?.length && t.receipts[0] ? (
                          <AttachmentLink file={t.receipts[0]} label="領収書" />
                        ) : t.attachments?.length && t.attachments[0] ? (
                          <AttachmentLink file={t.attachments[0]} label="添付" />
                        ) : t.source === 'recurring' ? (
                          <span className="hint">繰り返し支払い</span>
                        ) : (
                          <span className="badge b-ng">なし</span>
                        )}
                      </td>
                      <td className="hint">
                        {t.source === 'recurring'
                          ? '自動計上'
                          : (userNames.get(t.created_by) ?? '—')}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </section>
  );
}

/** tbody 直下に複数行を返すためのフラグメント */
function FragmentRows({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}
